import type { PageContext } from '../../lib/types';

type DesignContext = Pick<PageContext, 'path' | 'query'>;
export type DesignVersion = 'v1' | 'v2';
const preferenceKey = 'nodes:design-version';

export function designVersion(context: DesignContext): DesignVersion {
  if (context.query.design === 'v1' || context.query.design === 'v2') return context.query.design;
  try { return localStorage.getItem(preferenceKey) === 'v1' ? 'v1' : 'v2'; }
  catch { return 'v2'; }
}

// All application surfaces share the design system, including screens that
// still use an HTML renderer. The working Canvas retains its own interface.
export function usesDesignSystem(context: DesignContext): boolean {
  if (designVersion(context) !== 'v2') return false;
  const canvas = /^\/projects\/[^/]+\/flows\/[^/]+$/.test(context.path) || /^\/flows\/[^/]+$/.test(context.path);
  return !canvas || context.query.mode === 'app';
}

// Dedicated React layouts remain limited to the screens already migrated.
export function usesDesignV2(context: DesignContext): boolean {
  if (designVersion(context) !== 'v2') return false;
  const { path, query } = context;
  if (/^\/projects\/[^/]+$/.test(path)) return !['overview', 'assets', 'data'].includes(query.tab);
  if (/^\/brands\/[^/]+$/.test(path)) return !['projects', 'settings'].includes(query.tab);
  if (/^\/projects\/[^/]+\/flows\/[^/]+$/.test(path)) return query.mode === 'app';
  if (/^\/app\/[^/]+$/.test(path)) return true;
  if (/^\/assets\/[^/]+$/.test(path)) return true;
  return ['/', '/projects', '/brands', '/assets', '/library', '/flows', '/apps', '/use-as-app', '/executions', '/settings', '/knowledge'].includes(path)
    || /^\/executions\/[^/]+$/.test(path);
}

export function switchDesignVersion(context: DesignContext): void {
  const next = designVersion(context) === 'v2' ? 'v1' : 'v2';
  try { localStorage.setItem(preferenceKey, next); } catch { /* The URL still allows switching when storage is unavailable. */ }
  const query = new URLSearchParams({ ...context.query, design: next });
  location.hash = context.path + '?' + query.toString();
}
